import { useCallback, useState } from 'react';

export type SetValueInterface<T> = <K extends keyof T>(
  key: K,
  value: T[K],
) => void;

export type SetValueInterfaceParams<T> = Parameters<SetValueInterface<T>>;

export type SetStateInterface<T> = (
  newState: Partial<T> | ((prev: T) => Partial<T>),
) => void;

export type UseStatesInterface<T> = {
  state: T;
  setValue: SetValueInterface<T>;
  setState: SetStateInterface<T>;
  initialize: () => void;
};

const useStates = <T extends {}>(
  initialForm: T | (() => T),
): UseStatesInterface<T> => {
  const [state, set] = useState<T>(initialForm);

  const setValue = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    set((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const setState = useCallback(
    (newState: Partial<T> | ((prev: T) => Partial<T>)) => {
      if (typeof newState === 'function') {
        set((prev) => {
          return {
            ...prev,
            ...newState(prev),
          };
        });
        return;
      }
      set((prev) => ({
        ...prev,
        ...newState,
      }));
    },
    [],
  );

  const initialize = () => {
    set(initialForm);
  };

  return {
    state,
    setValue,
    setState,
    initialize,
  };
};

export default useStates;
